import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { notification, Row, Col, Popconfirm } from 'antd';
import {
  UploadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  EyeOutlined,
  DeleteOutlined,
  RocketOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { formatDateTime } from '../../utils/helpers';
import config from '../../config';
import { SectionDivider, ScrollableContent, PureSpectrumCard } from './CampaignDetailStyle';
import { StatusBadgeWrapper, TableActionWrapper } from './CampaignsStyle';
import { getStudyDetailStatusConfig, getStudyDetailStatusIcon } from '../../Components/StatusTag';
import {
  ThemeButton,
  ThemeModal,
  ThemeUploadDragDrop,
  ThemeDivider,
  ThemeTag,
  ThemeTable,
  ThemeFilterBtn,
} from '../Styles/Theme';
import {
  previewEmailUploadApi,
  previewEmailUploadReset,
  confirmEmailUploadApi,
  confirmEmailUploadReset,
  getEmailInvitesApi,
  getEmailInvitesReset,
  getEmailInviteStatsApi,
  getEmailInviteStatsReset,
  resendEmailApi,
  resendEmailReset,
  resendFailedEmailsApi,
  resendFailedEmailsReset,
  bulkResendEmailsApi,
  bulkResendEmailsReset,
  deleteParticipantApi,
  deleteParticipantReset,
} from '../../store/actions/emailInviteAction';
import Lottie from 'lottie-react';
import loaderJson from '../../jsons/upload.json';

const EmailInviteSection = (props) => {
  const {
    campaignId,
    campaign,
    canUpload,
    canResend,
    canBulkResend,
    canDelete,
    // Action permissions and handlers for email-invite study type
    canLaunch,
    canPause,
    canResume,
    canClose,
    onLaunch,
    onPause,
    onResume,
    onClose,
    launchLoading,
    pauseLoading,
    resumeLoading,
    closeLoading,
    // Redux state
    previewState,
    confirmState,
    emailInvitesState,
    emailStatsState,
    resendEmailState,
    resendFailedState,
    bulkResendState,
    deleteParticipantState,
    // Redux actions
    previewUpload,
    previewReset,
    confirmUpload,
    confirmReset,
    getEmailInvites,
    getEmailInvitesResetAction,
    getEmailInviteStats,
    getEmailInviteStatsResetAction,
    resendEmail,
    resendEmailResetAction,
    resendFailedEmails,
    resendFailedReset,
    bulkResendEmails,
    bulkResendReset,
    deleteParticipant,
    deleteParticipantResetAction,
  } = props;

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [listModalVisible, setListModalVisible] = useState(false);
  const [uploadStep, setUploadStep] = useState('upload');
  const [previewData, setPreviewData] = useState(null);
  const [page, setPage] = useState(1);
  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    if (campaignId) {
      getEmailInviteStats(campaignId);
    }
    return () => {
      getEmailInvitesResetAction();
      getEmailInviteStatsResetAction();
    };
  }, [campaignId]);

  // Handle preview response
  useEffect(() => {
    if (previewState.apiState === 'success') {
      const data = previewState.data?.data;
      setPreviewData(data);
      setUploadStep('preview');
      previewReset();
    } else if (previewState.apiState === 'error') {
      notification.error({
        message: 'Failed to process file',
        description: previewState.message,
      });
      setSelectedFile(null);
      previewReset();
    }
  }, [previewState.apiState]);

  // Handle confirm response
  useEffect(() => {
    if (confirmState.apiState === 'success') {
      const responseData = confirmState.data?.data || confirmState.data;
      const emailsQueued = responseData?.emailsQueued;

      notification.success({
        message: emailsQueued ? 'Participants Added' : 'Invite Sent',
        description: confirmState.message,
      });
      confirmReset();
      handleCloseUploadModal();
      getEmailInviteStats(campaignId);
    } else if (confirmState.apiState === 'error') {
      notification.error({
        message: 'Failed to send invites',
        description: confirmState.message,
      });
      confirmReset();
    }
  }, [confirmState.apiState]);

  // Handle resend email response
  useEffect(() => {
    if (resendEmailState.apiState === 'success') {
      notification.success({ message: 'Email resent successfully' });
      resendEmailResetAction();
      setResendingId(null);
      getEmailInviteStats(campaignId);
      getEmailInvites(campaignId, { page, limit: 10 });
    } else if (resendEmailState.apiState === 'error') {
      notification.error({
        message: 'Failed to resend email',
        description: resendEmailState.message,
      });
      resendEmailResetAction();
      setResendingId(null);
    }
  }, [resendEmailState.apiState]);

  // Handle resend failed response
  useEffect(() => {
    if (resendFailedState.apiState === 'success') {
      notification.success({
        message: 'Failed emails resent',
        description: resendFailedState.message,
      });
      resendFailedReset();
      getEmailInviteStats(campaignId);
      getEmailInvites(campaignId, { page: 1, limit: 10 });
    } else if (resendFailedState.apiState === 'error') {
      notification.error({
        message: 'Failed to resend emails',
        description: resendFailedState.message,
      });
      resendFailedReset();
    }
  }, [resendFailedState.apiState]);

  // Handle bulk resend response
  useEffect(() => {
    if (bulkResendState.apiState === 'success') {
      const data = bulkResendState.data?.data || {};
      notification.success({
        message: 'Bulk resend completed',
        description: `${data.sent || 0} emails sent${data.failed > 0 ? `, ${data.failed} failed` : ''}`,
      });
      bulkResendReset();
      getEmailInviteStats(campaignId);
      getEmailInvites(campaignId, { page, limit: 10 });
    } else if (bulkResendState.apiState === 'error') {
      notification.error({
        message: 'Failed to bulk resend emails',
        description: bulkResendState.message,
      });
      bulkResendReset();
    }
  }, [bulkResendState.apiState]);

  // Handle delete participant response
  useEffect(() => {
    if (deleteParticipantState.apiState === 'success') {
      notification.success({ message: 'Participant removed successfully' });
      deleteParticipantResetAction();
      setDeletingId(null);
      getEmailInviteStats(campaignId);
      getEmailInvites(campaignId, { page, limit: 10 });
    } else if (deleteParticipantState.apiState === 'error') {
      notification.error({
        message: 'Failed to remove participant',
        description: deleteParticipantState.message,
      });
      deleteParticipantResetAction();
      setDeletingId(null);
    }
  }, [deleteParticipantState.apiState]);

  const stats = emailStatsState.data?.data || {};
  const invites = emailInvitesState.data?.data?.invites || [];
  const pagination = emailInvitesState.data?.data?.pagination || {};

  const handleDownloadSample = () => {
    window.open(`${config.api.base_url}/email-invites/sample`, '_blank');
  };

  const handleCloseUploadModal = () => {
    setUploadModalVisible(false);
    setUploadStep('upload');
    setPreviewData(null);
    setSelectedFile(null);
  };

  const handleOpenListModal = () => {
    getEmailInvites(campaignId, { page: 1, limit: 10 });
    setPage(1);
    setListModalVisible(true);
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const extension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['csv', 'xls', 'xlsx'];

    if (!validExtensions.includes(extension)) {
      notification.error({
        message: 'Invalid file type',
        description: 'Please upload a CSV or Excel file (.csv, .xls, .xlsx)',
      });
      return;
    }

    setSelectedFile(file);
    previewUpload(campaignId, file);
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = '';
  };

  const handleConfirmSend = () => {
    if (!previewData?.valid?.length) return;
    confirmUpload(campaignId, previewData.valid, previewData.filename);
  };

  const handleResendEmail = (respondentId) => {
    setResendingId(respondentId);
    resendEmail(respondentId);
  };

  const handleResendFailed = () => {
    resendFailedEmails(campaignId);
  };

  const handleBulkResend = () => {
    bulkResendEmails(campaignId);
  };

  const handleDeleteParticipant = (respondentId) => {
    setDeletingId(respondentId);
    deleteParticipant(respondentId);
  };

  // Remove contact from preview before sending
  const handleRemoveFromPreview = (email) => {
    if (!previewData) return;

    const updatedValid = previewData.valid.filter((contact) => contact.email !== email);

    setPreviewData({
      ...previewData,
      valid: updatedValid,
      summary: {
        ...previewData.summary,
        validCount: updatedValid.length,
        totalCount: updatedValid.length + (previewData.summary?.invalidCount || 0),
      },
    });

    notification.info({
      message: 'Contact Removed',
      description: `${email} has been removed from the list.`,
    });
  };

  const handleTableChange = (paginationConfig) => {
    const newPage = paginationConfig.current;
    setPage(newPage);
    getEmailInvites(campaignId, { page: newPage, limit: 10 });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Generate dynamic columns for preview table - email and name are fixed, other columns scroll
  const getPreviewColumns = (sampleRow) => {
    const deleteColumn = {
      title: '',
      key: 'delete',
      width: 40,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Remove Contact"
          description="Are you sure you want to remove this contact?"
          onConfirm={() => handleRemoveFromPreview(record.email)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <ThemeFilterBtn
            shape="circle"
            size="small"
            icon={<img src="/images/delete-icon.svg" alt="Delete" style={{ width: 12, height: 12 }} />}
            title="Remove"
          />
        </Popconfirm>
      ),
    };

    if (!sampleRow) return [
      { title: 'Email', dataIndex: 'email', key: 'email', width: 180, fixed: 'left' },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 120, fixed: 'left' },
      deleteColumn,
    ];

    const fixedColumns = [
      { title: 'Email', dataIndex: 'email', key: 'email', width: 180, fixed: 'left' },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 120, fixed: 'left' },
    ];

    // Get other columns from sample row (excluding email and name)
    const otherKeys = Object.keys(sampleRow).filter(key => !['email', 'name'].includes(key.toLowerCase()));
    const dynamicColumns = otherKeys.map(key => ({
      title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      dataIndex: key,
      key: key,
      width: 120,
      ellipsis: true,
    }));

    return [...fixedColumns, ...dynamicColumns, deleteColumn];
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'invite_name',
      key: 'name',
      width: 120,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'invite_email',
      key: 'email',
      width: 180,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'PIN',
      dataIndex: 'pin',
      key: 'pin',
      width: 90,
      render: (pin) => <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{pin}</code>,
    },
    {
      title: 'Email Status',
      dataIndex: 'email_status',
      key: 'email_status',
      width: 110,
      render: (status) => {
        const statusMap = {
          sent: { color: 'success', icon: <CheckCircleOutlined />, label: 'Sent' },
          pending: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending' },
          failed: { color: 'error', icon: <ExclamationCircleOutlined />, label: 'Failed' },
        };
        const c = statusMap[status] || { color: 'default', label: status || '-' };
        return (
          <ThemeTag color={c.color}>
            {c.icon} {c.label}
          </ThemeTag>
        );
      },
    },
    {
      title: 'Survey Status',
      dataIndex: 'call_status',
      key: 'call_status',
      width: 110,
      render: (status) => {
        const statusMap = {
          completed: { color: 'success', label: 'Completed' },
          in_progress: { color: 'processing', label: 'In Progress' },
          pending: { color: 'default', label: 'Pending' },
          failed: { color: 'error', label: 'Failed' },
          incomplete: { color: 'warning', label: 'Incomplete' },
        };
        const c = statusMap[status] || { color: 'default', label: status || '-' };
        return <ThemeTag color={c.color}>{c.label}</ThemeTag>;
      },
    },
    {
      title: 'Sent At',
      dataIndex: 'email_sent_at',
      key: 'email_sent_at',
      width: 140,
      render: (date) => formatDateTime(date),
    },
    ...((canResend || canDelete)
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 90,
            fixed: 'right',
            align: 'right',
            render: (_, record) => {
              // Resend condition: email was sent (not pending) AND survey not completed
              const canShowResend = canResend && record.email_status !== 'pending' && record.call_status !== 'completed';

              return (
                <TableActionWrapper style={{ justifyContent: 'flex-end' }}>
                  {canShowResend && (
                    <Popconfirm
                      title="Resend Email"
                      description="Are you sure you want to resend this email?"
                      onConfirm={() => handleResendEmail(record._id)}
                      okText="Yes, Resend"
                      cancelText="Cancel"
                      okButtonProps={{ loading: resendingId === record._id }}
                    >
                      <ThemeFilterBtn
                        shape="circle"
                        size="medium"
                        icon={<ReloadOutlined spin={resendingId === record._id} />}
                        title="Resend Email"
                      />
                    </Popconfirm>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title="Remove Participant"
                      description="Are you sure you want to remove this participant?"
                      onConfirm={() => handleDeleteParticipant(record._id)}
                      okText="Yes, Remove"
                      cancelText="Cancel"
                      okButtonProps={{ loading: deletingId === record._id, danger: true }}
                    >
                      <ThemeFilterBtn
                        shape="circle"
                        size="medium"
                        icon={<img src="/images/delete-icon.svg" alt="Delete" />}
                        title="Remove Participant"
                        loading={deletingId === record._id}
                      />
                    </Popconfirm>
                  )}
                </TableActionWrapper>
              );
            },
          },
        ]
      : []),
  ];

  const allValid = previewData?.summary?.invalidCount === 0;
  const hasInvalid = previewData?.summary?.invalidCount > 0;
  const isProcessing = previewState.apiState === 'loading';
  const isSending = confirmState.apiState === 'loading';

  return (
    <>
      {/* Status Card - Shows campaign status and stats */}
      {campaign && (() => {
        const statusConfig = getStudyDetailStatusConfig(campaign.status || 'saved');
        return (
          <PureSpectrumCard
            className="mb-4"
            style={{
              background: statusConfig.cardBackground,
              border: `1px solid ${statusConfig.cardBorder}`,
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col flex="auto">
                <h3>Study</h3>
                <p>
                  {stats.total > 0
                    ? `${stats.emailSent || 0} sent · ${stats.emailPending || 0} pending · ${stats.surveyCompleted || 0} completed`
                    : 'Upload contacts to send invitations'}
                </p>
              </Col>
              <Col>
                <StatusBadgeWrapper
                  style={{
                    backgroundColor: '#FFFFFF',
                    color: statusConfig.color,
                    border: `1px solid ${statusConfig.cardBorder}`,
                  }}
                >
                  <span className="status-icon">{getStudyDetailStatusIcon(campaign.status)}</span>
                  <span>{statusConfig.text}</span>
                </StatusBadgeWrapper>
              </Col>
            </Row>
          </PureSpectrumCard>
        );
      })()}

      <SectionDivider />

      {/* Stats Row */}
      <div className="justifyBetween mb-5" align="center">
        <div className="title">Total Invites</div>
        <div className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {stats.total > 0 && (
            <EyeOutlined
              style={{ cursor: 'pointer', color: '#3d2969' }}
              onClick={handleOpenListModal}
              title="View Invites"
            />
          )}
          {stats.total || 0}
        </div>
      </div>
      <div className="justifyBetween mb-5" align="center">
        <div className="title">Emails Sent</div>
        <div className="value">{stats.emailSent || 0}</div>
      </div>
      <div className="justifyBetween mb-5" align="center">
        <div className="title">Surveys Completed</div>
        <div className="value">{stats.surveyCompleted || 0}</div>
      </div>
      {stats.emailFailed > 0 && (
        <div className="justifyBetween mb-5" align="center">
          <div className="title">Failed Emails</div>
          <div className="value" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ff4d4f' }}>
            {stats.emailFailed}
            {canResend && (
              <Popconfirm
                title="Resend Failed Emails"
                description={`Are you sure you want to resend ${stats.emailFailed} failed email${stats.emailFailed > 1 ? 's' : ''}?`}
                onConfirm={handleResendFailed}
                okText="Yes, Resend"
                cancelText="Cancel"
                okButtonProps={{ loading: resendFailedState.apiState === 'loading' }}
              >
                <ReloadOutlined
                  style={{ cursor: 'pointer', marginLeft: 4 }}
                  spin={resendFailedState.apiState === 'loading'}
                  title="Resend Failed"
                />
              </Popconfirm>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '12px', marginTop: 16 }}>
        {canUpload && (
          <ThemeButton
            size="large"
            className="noBackground"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            Upload
          </ThemeButton>
        )}

        {canPause && (
          <ThemeButton
            size="large"
            className="primary"
            icon={<PauseCircleOutlined />}
            onClick={onPause}
            loading={pauseLoading}
          >
            Pause Study
          </ThemeButton>
        )}

        {canResume && (
          <ThemeButton
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={onResume}
            loading={resumeLoading}
          >
            Resume Study
          </ThemeButton>
        )}

        {canClose && (
          <ThemeButton
            size="large"
            className="danger"
            icon={<CloseCircleOutlined />}
            onClick={onClose}
            loading={closeLoading}
          >
            Close Study
          </ThemeButton>
        )}

        {canLaunch && (
          <ThemeButton
            size="large"
            icon={<RocketOutlined />}
            onClick={onLaunch}
            loading={launchLoading}
          >
            Launch Study
          </ThemeButton>
        )}
      </div>

      {/* Upload Modal */}
      <ThemeModal
        title={
          <>
            Upload Contacts <br />
            <span className="uploadDocumentSubTitle">Import your contact list</span>
          </>
        }
        style={{ top: 20 }}
        open={uploadModalVisible}
        onCancel={() => {
          if (!isProcessing && !isSending) {
            handleCloseUploadModal();
          }
        }}
        maskClosable={false}
        centered
        width={600}
        footer={
          uploadStep === 'preview' && previewData ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '8px 0' }}>
              <ThemeButton
                size="large"
                className="noBackground"
                onClick={() => {
                  setUploadStep('upload');
                  setPreviewData(null);
                  setSelectedFile(null);
                }}
                disabled={isSending}
              >
                Upload Different File
              </ThemeButton>
              <ThemeButton
                size="large"
                type="primary"
                onClick={handleConfirmSend}
                loading={isSending}
                disabled={!previewData?.valid?.length || isSending}
              >
                Send Invite
                <SendOutlined style={{ marginLeft: 8 }} />
              </ThemeButton>
            </div>
          ) : null
        }
      >
        {uploadStep === 'upload' && (
          <>
            <style>
              {`
                .email-upload-area .ant-upload-drag,
                .email-upload-area .ant-upload,
                .email-upload-area .ant-upload-btn {
                  cursor: default !important;
                }
              `}
            </style>
            <div className="email-upload-area">
              <ThemeUploadDragDrop
                openFileDialogOnClick={false}
                beforeUpload={(file) => {
                  handleFileSelect(file);
                  return false;
                }}
                showUploadList={false}
                multiple={false}
                disabled={isProcessing}
                accept=".csv,.xls,.xlsx"
              >
              <div className="iconWrapper">
                {isProcessing ? (
                  <div style={{ width: 80, height: 80, margin: '0 auto' }}>
                    <Lottie animationData={loaderJson} loop={true} />
                  </div>
                ) : (
                  <img className="m-auto" src="/images/drag-icon.svg" alt="Upload" />
                )}
              </div>
              <div className="uploadTitle mt-4">
                {isProcessing ? 'Processing file...' : 'Drag CSV/Excel file to upload'}
              </div>
              <ThemeDivider>Or</ThemeDivider>
              <ThemeButton
                size="middle"
                shape="round"
                className="btnBorder noBackground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowseFiles();
                }}
                disabled={isProcessing}
                style={{ cursor: 'pointer' }}
              >
                Browse files
              </ThemeButton>
            </ThemeUploadDragDrop>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              accept=".csv,.xls,.xlsx"
              onChange={handleFileInputChange}
            />
            <div className="uploadTitle my-2">
              File must have columns: <strong>email</strong> (required), <strong>name</strong> (required)
            </div>

            {/* Selected file preview */}
            {selectedFile && !isProcessing && (
              <div style={{
                marginTop: 16,
                padding: 12,
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src="/images/csv-icon.svg" alt="file" style={{ width: 24, height: 24 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{selectedFile.name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{formatBytes(selectedFile.size)}</div>
                  </div>
                </div>
                <DeleteOutlined
                  style={{ color: '#ff4d4f', cursor: 'pointer' }}
                  onClick={() => setSelectedFile(null)}
                />
              </div>
            )}
          </>
        )}

        {uploadStep === 'preview' && previewData && (
          <ScrollableContent maxHeight="60vh">
            <div
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                marginBottom: 16,
                background: allValid ? 'rgba(27, 136, 68, 0.05)' : '#fffbe6',
                border: `1px solid ${allValid ? 'rgba(27, 136, 68, 0.2)' : '#faad14'}`,
                color: allValid ? '#1B8844' : '#ad6800',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {allValid
                ? `✓ All ${previewData.summary.validCount} contacts are valid!`
                : `${previewData.summary.validCount} valid, ${previewData.summary.invalidCount} invalid`}
            </div>

            {hasInvalid && (
              <>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: '#1f2430' }}>
                  Invalid Rows (will be skipped):
                </div>
                <div style={{ border: '1px solid #edeef3', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                  <ThemeTable
                    dataSource={previewData.invalid}
                    columns={[
                      { title: 'Row', dataIndex: 'row', key: 'row', width: 60 },
                      { title: 'Email', dataIndex: 'email', key: 'email', width: 180 },
                      { title: 'Name', dataIndex: 'name', key: 'name', width: 120 },
                      {
                        title: 'Errors',
                        dataIndex: 'errors',
                        key: 'errors',
                        render: (errors) => <ThemeTag color="error">{errors?.join(', ')}</ThemeTag>,
                      },
                    ]}
                    rowKey="row"
                    size="small"
                    pagination={false}
                    scroll={{ y: previewData.invalid.length > 5 ? 200 : undefined }}
                  />
                </div>
              </>
            )}

            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: '#1B8844' }}>
              Valid Contacts ({previewData.valid.length}):
            </div>
            <div style={{ border: '1px solid #edeef3', borderRadius: 10, overflow: 'hidden' }}>
              <ThemeTable
                dataSource={previewData.valid}
                columns={getPreviewColumns(previewData.valid[0])}
                rowKey="email"
                size="small"
                pagination={false}
                scroll={{
                  x: previewData.extraColumns?.length > 0 ? 500 : undefined,
                  y: previewData.valid.length > 10 ? 300 : undefined,
                }}
              />
            </div>
          </ScrollableContent>
        )}
      </ThemeModal>

      {/* View Invites Modal */}
      <ThemeModal
        title={
          <>
            Custom Upload <br />
            <span className="uploadDocumentSubTitle">View all sent invitations</span>
          </>
        }
        open={listModalVisible}
        onCancel={() => setListModalVisible(false)}
        maskClosable={false}
        width={950}
        footer={null}
        centered
      >
        {canBulkResend && stats.resendEligible > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Popconfirm
              title="Bulk Resend Emails"
              description={`Resend to ${stats.resendEligible} eligible participant${stats.resendEligible > 1 ? 's' : ''}?`}
              onConfirm={handleBulkResend}
              okText="Resend"
              cancelText="Cancel"
              okButtonProps={{ loading: bulkResendState.apiState === 'loading' }}
            >
              <ThemeButton
                icon={<ReloadOutlined spin={bulkResendState.apiState === 'loading'} />}
                loading={bulkResendState.apiState === 'loading'}
              >
                Bulk Resend
              </ThemeButton>
            </Popconfirm>
          </div>
        )}
        <div style={{ border: '1px solid #edeef3', borderRadius: 10, overflow: 'hidden' }}>
          <ThemeTable
            dataSource={invites}
            columns={columns}
            rowKey="_id"
            size="small"
            loading={emailInvitesState.apiState === 'loading'}
            scroll={{ x: 800 }}
            pagination={{
              current: page,
              pageSize: 10,
              total: pagination.total,
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invites`,
            }}
            onChange={handleTableChange}
          />
        </div>
      </ThemeModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  previewState: state.emailInvite.previewEmailUploadState,
  confirmState: state.emailInvite.confirmEmailUploadState,
  emailInvitesState: state.emailInvite.getEmailInvitesState,
  emailStatsState: state.emailInvite.getEmailInviteStatsState,
  resendEmailState: state.emailInvite.resendEmailState,
  resendFailedState: state.emailInvite.resendFailedEmailsState,
  bulkResendState: state.emailInvite.bulkResendEmailsState,
  deleteParticipantState: state.emailInvite.deleteParticipantState,
});

const mapDispatchToProps = (dispatch) => ({
  previewUpload: (id, file) => dispatch(previewEmailUploadApi(id, file)),
  previewReset: () => dispatch(previewEmailUploadReset()),
  confirmUpload: (id, contacts, filename) => dispatch(confirmEmailUploadApi(id, contacts, filename)),
  confirmReset: () => dispatch(confirmEmailUploadReset()),
  getEmailInvites: (id, params) => dispatch(getEmailInvitesApi(id, params)),
  getEmailInvitesResetAction: () => dispatch(getEmailInvitesReset()),
  getEmailInviteStats: (id) => dispatch(getEmailInviteStatsApi(id)),
  getEmailInviteStatsResetAction: () => dispatch(getEmailInviteStatsReset()),
  resendEmail: (id) => dispatch(resendEmailApi(id)),
  resendEmailResetAction: () => dispatch(resendEmailReset()),
  resendFailedEmails: (id, batchId) => dispatch(resendFailedEmailsApi(id, batchId)),
  resendFailedReset: () => dispatch(resendFailedEmailsReset()),
  bulkResendEmails: (id) => dispatch(bulkResendEmailsApi(id)),
  bulkResendReset: () => dispatch(bulkResendEmailsReset()),
  deleteParticipant: (id) => dispatch(deleteParticipantApi(id)),
  deleteParticipantResetAction: () => dispatch(deleteParticipantReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailInviteSection);
